import React from 'react';
import {OperatorGeneralSettings, OperatorGeneralSettingsData} from "types/Operator";
import {Form, Formik, FormikHelpers} from "formik";
import Field from "components/Form/Field";
import FormControls from "components/FormControls";
import Selector from "components/Form/Selector";
import {countries} from "country-data-list";
import {useOperator} from "../../../context/OperatorContext";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";

// Types ----

type GeneralFormProps = {
  settings?: OperatorGeneralSettings | null
  onSubmit: (values: OperatorGeneralSettingsData, helpers: FormikHelpers<OperatorGeneralSettingsData>) => any
}

// Helpers ----

const countryOptions = countries
  .all
  .filter(country => !!country.alpha3)
  .map(country => ({
    id: country.name,
    name: country.name,
  }))

function GeneralForm({onSubmit, settings}: GeneralFormProps) {
  const {settings: allSettings} = useOperator();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required"),
    phone: Yup.string().max(15, 'Maximum of 15 characters'),
    address: Yup.object().shape({
      line1: Yup.string().required('This field is required').max(250, 'Maximum of 250 characters'),
      line2: Yup.string().max(250, 'Maximum of 250 characters'),
      line3: Yup.string().max(250, 'Maximum of 250 characters'),
      city: Yup.string().required('This field is required').max(100, 'Maximum of 100 characters'),
      postcode: Yup.string().required('This field is required').max(20, 'Maximum of 20 characters'),
      country: Yup.string().required('This field is required'),
    }),
  })

  const initialValues: OperatorGeneralSettingsData = {
    name: settings?.name || '',
    email: settings?.email || '',
    marketing_email: settings?.marketing_email || '',
    phone: settings?.phone || '',
    address: {
      line1: settings?.address?.line1 || '',
      line2: settings?.address?.line2 || '',
      line3: settings?.address?.line3 || '',
      city: settings?.address?.city || '',
      postcode: settings?.address?.postcode || '',
      country: settings?.address?.country || '',
    }
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {formik => (
        <section aria-labelledby="payment-details-heading">
          <Form onKeyDown={event => {
            if (event.ctrlKey && event.key === 'Enter') formik.handleSubmit()
          }}>
            <ErrorNotification formik={formik} />

            <div className="shadow sm:rounded-md">
              <div className="bg-white py-6 px-4 sm:p-6 sm:rounded-md">
                <div>
                  <h2 id="payment-details-heading" className="text-lg leading-6 font-medium text-gray-900">
                    General Details
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Update your operator's name and contact details
                  </p>
                </div>

                <div className="mt-6 grid grid-cols-6 gap-6">

                  {/* Name */}
                  <div className="col-span-6 md:col-span-3">
                    <Field
                      type="text"
                      label="Name *"
                      name="name"
                      id="name"
                      max={255}
                      autoComplete="off"
                    />
                  </div>

                  {/* Email */}
                  <div className="col-span-6 md:col-span-2 md:col-start-1">
                    <Field
                      type="email"
                      label="Email Address"
                      name="email"
                      id="email"
                      max={255}
                      autoComplete="off"
                      disabled
                    />
                  </div>

                  {/* Marketing Email */}
                  <div className="col-span-6 md:col-span-2">
                    <Field
                      type="email"
                      label="Marketing Email Address"
                      name="marketing_email"
                      id="marketing_email"
                      max={255}
                      autoComplete="off"
                      disabled
                    />
                  </div>

                  {/* Phone Number */}
                  <div className="col-span-6 md:col-span-2 md:col-start-1">
                    <Field
                      type="text"
                      label="Phone Number"
                      hint="Optional"
                      name="phone"
                      id="phone"
                      min={5}
                      max={15}
                      autoComplete="off"
                    />
                  </div>

                  {/* Address */}
                  <div className="col-span-6 lg:col-span-4 lg:col-start-1">
                    <Field
                      type="text"
                      label="Address (Line 1) *"
                      name="address.line1"
                      id="address.line1"
                      autoComplete="address-line1"
                      maxLength="255"
                    />
                  </div>

                  <div className="col-span-6 lg:col-span-4">
                    <Field
                      type="text"
                      label="Address (Line 2)"
                      hint="Optional"
                      name="address.line2"
                      id="address.line2"
                      autoComplete="address-line2"
                      maxLength="255"
                    />
                  </div>

                  <div className="col-span-6 lg:col-span-4">
                    <Field
                      type="text"
                      label="Address (Line 3)"
                      hint="Optional"
                      name="address.line3"
                      id="address.line3"
                      autoComplete="address-line3"
                      maxLength="255"
                    />
                  </div>

                  <div className="col-span-6 lg:col-span-2 lg:col-start-1">
                    <Field
                      type="text"
                      label="City *"
                      name="address.city"
                      id="address.city"
                      autoComplete="address-level2"
                      maxLength="255"
                    />
                  </div>

                  <div className="col-span-2">
                    <Field
                      type="text"
                      label={allSettings?.localisation.locale === 'en-US' ? "Zip Code *" : "Post Code *"}
                      name="address.postcode"
                      id="address.postcode"
                      autoComplete="postal-code"
                      maxLength="255"
                    />
                  </div>

                  <div className="col-span-6 lg:col-span-2 lg:col-start-1">
                    <Selector
                      label="Country *"
                      items={countryOptions}
                      selectedId={initialValues.address?.country}
                      onChange={country => formik.setFieldValue('address.country', country.id)}
                    />
                  </div>
                </div>
              </div>

              <FormControls formik={formik} compact withoutCancel/>
            </div>
          </Form>
        </section>
      )}
    </Formik>
  );
}

export default GeneralForm;